import { useCallback, useSyncExternalStore } from 'react';
import type { BodyMetricRow } from '../../shared/queries';
import { languageTag } from '../../shared/language';
import type { MessageId } from '../../shared/text';

/**
 * The client's own body, as a small fixed set of numbers they type themselves.
 *
 * ## What this is, said plainly
 *
 * **There is no `body_metric` table, and `client-me` adds none** (its design
 * says so under Non-Goals). So what a client enters is held **on this device and
 * nowhere else**, the same arrangement as `./progress.ts` for a session's ticks
 * and `./presence.ts` for a message they typed, and for the same reasons:
 * settling a shape in a migration before anybody has used the screen is
 * expensive to undo, and `migrations-forward-only` means a wrong shape on `main`
 * is corrected by a second migration rather than a revert.
 *
 * **Nothing here is sent anywhere. No coach sees it. Nothing survives clearing
 * site data or opening the app on another phone.** The screen says exactly that,
 * permanently, under the form — a measurement is the most personal thing a
 * client types into this product, and a field that quietly kept it in a browser
 * while implying otherwise would be worse than no field.
 *
 * ## The set is fixed, and the refusals are the design
 *
 * Five entries, and there is no control anywhere to add a sixth. What is
 * **deliberately absent** matters more than what is here, and none of it is an
 * oversight:
 *
 *   * **no photograph of any kind** — no attach, no capture, no upload. There is
 *     nowhere in `BodyMetricRow` to put one;
 *   * **no body fat, no BMI, no computed index and no category.** Counsel's
 *     body-metrics ruling names them: a computed classification of a body is the
 *     first step towards software that calculates for a medical purpose. The
 *     hi-fi's profile screen draws "Body fat 22%" and it is not ported;
 *   * **no resting heart rate or any other physiological reading**, for the same
 *     reason. The hi-fi draws that too;
 *   * **no target, no ideal, no coach-set goal.** A coach cannot enter a
 *     measurement at all, let alone a number the client is supposed to reach;
 *   * **no age and no gender.** Age is one tick and no date of birth
 *     (`src/shared/consent.ts`); gender the product deliberately never acquired.
 *
 * The set itself is **provisional**: `proposal.md`'s first question for the
 * trainer is which measurements she actually uses and which one she would drop
 * because asking for it makes people feel worse. Nothing is stored, so changing
 * it costs one array.
 *
 * ## Failing safe
 *
 * Every read and write is wrapped. `localStorage` throws in a private window
 * with site data blocked, and returns text that is not ours after a version
 * skew. Both produce the empty state — nothing recorded — rather than a broken
 * screen.
 */

export type MeasurementId = 'weight' | 'waist' | 'hip' | 'arm' | 'thigh';

export interface MeasurementKind {
  id: MeasurementId;
  /** Its name, from the catalogue. Never a literal on a screen. */
  name: MessageId;
  /** `kg` or `cm`. Shown beside the field and beside the value. */
  unit: 'kg' | 'cm';
  /**
   * The range the field accepts. Not a judgement and not a target: it is the
   * band outside which a number is certainly a typo, and a rejected typo is
   * kinder than a chart with 640 kg in it.
   */
  min: number;
  max: number;
}

export const MEASUREMENTS: readonly MeasurementKind[] = [
  { id: 'weight', name: 'client.measurements.weight', unit: 'kg', min: 20, max: 300 },
  { id: 'waist', name: 'client.measurements.waist', unit: 'cm', min: 30, max: 250 },
  { id: 'hip', name: 'client.measurements.hip', unit: 'cm', min: 30, max: 250 },
  { id: 'arm', name: 'client.measurements.arm', unit: 'cm', min: 10, max: 100 },
  { id: 'thigh', name: 'client.measurements.thigh', unit: 'cm', min: 20, max: 150 },
];

export function measurementKind(id: MeasurementId): MeasurementKind {
  const kind = MEASUREMENTS.find((candidate) => candidate.id === id);
  // Unreachable for a `MeasurementId`; the throw is what makes the narrowing
  // honest rather than a non-null assertion the lint rules forbid.
  if (kind === undefined) throw new Error(`unknown measurement ${id}`);
  return kind;
}

/** One measurement, whether it came from the device or from a query. */
export interface Measurement {
  id: string;
  metric: MeasurementId;
  value: number;
  /** The calendar day it is against, as `YYYY-MM-DD`. */
  on: string;
}

function isMeasurementId(value: unknown): value is MeasurementId {
  return MEASUREMENTS.some((kind) => kind.id === value);
}

/* --------------------------------------------------------------- device --- */

/**
 * One key, with a version in it — the same arrangement `./progress.ts` and
 * `./presence.ts` use, so a later shape is recognised and ignored rather than
 * misread.
 */
const STORAGE_KEY = 'client.measurements.v1';

const EMPTY: readonly Measurement[] = [];

function isMeasurement(value: unknown): value is Measurement {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Partial<Measurement>;
  return (
    typeof row.id === 'string' &&
    isMeasurementId(row.metric) &&
    typeof row.value === 'number' &&
    Number.isFinite(row.value) &&
    typeof row.on === 'string'
  );
}

function parse(raw: string): readonly Measurement[] {
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? value.filter(isMeasurement) : EMPTY;
  } catch {
    return EMPTY;
  }
}

let cachedRaw: string | null = null;
let cached: readonly Measurement[] = EMPTY;

function readRaw(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Everything this device holds. Never null; an untouched device is empty. */
export function readMeasurements(): readonly Measurement[] {
  const raw = readRaw();
  if (raw === null) return EMPTY;
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cached = parse(raw);
  }
  return cached;
}

const listeners = new Set<() => void>();

function write(next: readonly Measurement[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A browser that refuses to remember loses the entry. It is not a reason to
    // fail the screen somebody is standing in front of.
  }
  cachedRaw = null;
  for (const listener of listeners) listener();
}

/**
 * Record what the client typed, against a day.
 *
 * Takes the whole form at once rather than one field at a time, because that is
 * what the client did: they filled in what they wanted and pressed save. Fields
 * left blank are absent from `values` and nothing is written for them — a blank
 * is not a zero.
 */
export function logMeasurements(
  values: Partial<Record<MeasurementId, number>>,
  on: string,
): Measurement[] {
  const stamp = Date.now();
  const added: Measurement[] = [];
  let index = 0;
  for (const kind of MEASUREMENTS) {
    const value = values[kind.id];
    if (value === undefined) continue;
    index += 1;
    added.push({
      id: `local-${kind.id}-${String(stamp)}-${String(index)}`,
      metric: kind.id,
      value,
      on,
    });
  }
  if (added.length > 0) write([...readMeasurements(), ...added]);
  return added;
}

/**
 * Forget every measurement this device holds.
 *
 * This is what withdrawing the measurements consent actually does — the spec
 * requires that turning a consent off deletes the content stored under it, and
 * on this device that content is exactly this. It is also what the tests use to
 * start from nothing.
 */
export function forgetMeasurements(): void {
  write(EMPTY);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** What this device holds, re-rendered when it changes anywhere in this tab. */
export function useDeviceMeasurements(): readonly Measurement[] {
  const getSnapshot = useCallback(() => readMeasurements(), []);
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}

/* --------------------------------------------------------------- series --- */

/** A query row, as a measurement. Unknown metric names are dropped, not guessed. */
export function fromRows(rows: readonly BodyMetricRow[]): Measurement[] {
  const out: Measurement[] = [];
  for (const row of rows) {
    if (!isMeasurementId(row.metric)) continue;
    out.push({ id: row.id, metric: row.metric, value: row.value, on: row.measured_on });
  }
  return out;
}

/**
 * One measurement's history, oldest first — whatever the database had, plus
 * whatever this device holds, by day.
 *
 * The merge is the same one the thread screen does with messages, and it exists
 * for the same reason: on real data the query half is empty and the device half
 * is everything, and no screen may branch on which build it is in.
 *
 * Ties are broken by the order they arrived, so two entries on the same day read
 * in the order they were made rather than in an arbitrary one.
 */
export function series(
  metric: MeasurementId,
  stored: readonly Measurement[],
  device: readonly Measurement[],
): Measurement[] {
  return [...stored, ...device]
    .filter((entry) => entry.metric === metric)
    .sort((a, b) => a.on.localeCompare(b.on));
}

/** The most recent one, or `null` when there are none. */
export function latest(entries: readonly Measurement[]): Measurement | null {
  return entries.length === 0 ? null : (entries[entries.length - 1] ?? null);
}

/**
 * The difference between the first and the last, or `null` when there are fewer
 * than two.
 *
 * **A number, and never a direction.** There is no `improved`, no `better`, no
 * boolean for "went the right way", because there is no right way: a weight
 * going up is a fact (`client-tone`). The screen renders it as a signed number
 * in the ordinary ink, with no arrow and no colour, and it is `null` — not zero,
 * not "no change yet" — when there is only one point, because a change needs two
 * and inventing one is exactly the inferred trend the spec forbids.
 */
export function change(entries: readonly Measurement[]): number | null {
  if (entries.length < 2) return null;
  const first = entries[0];
  const last = entries[entries.length - 1];
  if (first === undefined || last === undefined) return null;
  return round(last.value - first.value);
}

/** One decimal, which is the precision a bathroom scale and a tape measure have. */
export function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * A measurement as text.
 *
 * Spanish writes 64,4 and not 64.4, and the language is the product's, never the
 * reader's machine — the same rule `src/shared/formatDate.ts` states at length
 * for dates. This is the only place in the product that renders a decimal, so it
 * is one function rather than a rule nobody can enforce.
 *
 * The sign is written explicitly for a change, because "−1,2" and "1,2" are
 * different facts and a reader should not have to work out which one they are
 * looking at. A sign is not a direction arrow and it is not coloured.
 */
export function formatMeasurement(value: number, unit: string, withSign = false): string {
  const formatted = new Intl.NumberFormat(languageTag(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    signDisplay: withSign ? 'exceptZero' : 'auto',
  }).format(value);
  return `${formatted} ${unit}`;
}

/**
 * Parse what somebody typed into a field.
 *
 * `null` means "they left it blank", which is not an error; `NaN` means they
 * typed something that is not a number, which is. `NaN` is a `number`, so the
 * signature is `number | null` and the caller tells the two apart with
 * `Number.isNaN` — spelling it out in the return type is a duplicate constituent
 * and the lint rules say so.
 *
 * A comma is accepted because a Spanish keyboard produces one and a form that
 * rejected it would be rejecting the language it is written in.
 */
export function parseEntered(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(',', '.'));
  return Number.isFinite(value) ? round(value) : Number.NaN;
}

/** Whether a parsed number is inside the kind's band. See `MeasurementKind`. */
export function inRange(kind: MeasurementKind, value: number): boolean {
  return value >= kind.min && value <= kind.max;
}
